/**
 * Planner WebMCP commands — Table half.
 *
 * Four tools: the shared read (`planner_find_articles`, written once here for
 * all three lanes) and three writes that consume ids from it.
 *
 * This module deliberately exports the SCHEMAS, RESOLVERS, DIFFS and FORMATTERS
 * rather than assembled `Command` objects. Assembly needs two `contract.ts`
 * widenings that are not mine to land: `CommandName` is still `bb_${string}`,
 * and `Consequence` has no `spends-budget` or `writes-live-site`. Everything
 * below compiles and is unit-testable without them; `plannerCommands.ts` will
 * be a thin `defineCommand` wrapper once the contract lands.
 *
 * Seams (`read.findArticles`, `write.createArticle`, `write.publishArticle`,
 * `write.regenerate`), `plannerDates.ts` and `handshake.ts` arrive on the
 * Calendar commit; they are referenced through the local structural types at
 * the bottom so this file does not fork a second definition of them.
 */
import { z } from 'zod';
import type {
  Diff,
  PlannerArticleSummary,
  Receipt,
  Refusal,
  TenantId,
} from '../contract';

/* -------------------------------------------------------------------------- */
/* Shared vocabulary                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The statuses an agent may filter on. A closed enum rather than a free string
 * because the model cannot see the column and would otherwise invent values;
 * a miss returns the legal set in the refusal rather than an empty list that
 * reads as "no such articles".
 */
export const ARTICLE_STATUSES = [
  'pending',
  'in_progress',
  'ready',
  'needs_review',
  'completed',
  'completed_degraded',
  'published',
  'failed',
] as const;
export type ArticleStatus = (typeof ARTICLE_STATUSES)[number];

/** `YYYY-MM-DD`. Chrome validates nothing, so this is the only enforcement. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** `YYYY-MM`, expanded to an inclusive month end by the date helper. */
const ISO_MONTH = /^\d{4}-\d{2}$/;

/** Cap on rows rendered into a Receipt. Chrome truncates long results and the
 *  brief caps tool output at ~1.5K characters; 25 lines sits safely inside it. */
export const FIND_ROW_CAP = 25;

/* -------------------------------------------------------------------------- */
/* Schemas — module scope, defined once                                       */
/*                                                                            */
/* big-calendar re-registers its tools on every store tick because it passes  */
/* inline schema literals. Hoisting means one object identity for the life of */
/* the document.                                                              */
/* -------------------------------------------------------------------------- */

export const findArticlesArgs = z
  .object({
    /** Exact ids. Wins over every other filter when present. */
    ids: z.array(z.string().uuid()).min(1).max(100).optional(),
    status: z.array(z.enum(ARTICLE_STATUSES)).min(1).optional(),
    /** Case-insensitive substring over title and keyword. */
    title: z.string().min(2).max(120).optional(),
    from: z.string().regex(ISO_DATE, 'expected YYYY-MM-DD, e.g. 2026-09-01').optional(),
    to: z.string().regex(ISO_DATE, 'expected YYYY-MM-DD, e.g. 2026-09-30').optional(),
    /** Convenience for a whole month; expanded inclusively by plannerDates. */
    month: z.string().regex(ISO_MONTH, 'expected YYYY-MM, e.g. 2026-09').optional(),
  })
  .strict();
export type FindArticlesArgs = z.infer<typeof findArticlesArgs>;

export const createArticleArgs = z
  .object({
    keyword: z.string().min(3).max(160),
    title: z.string().min(3).max(200).optional(),
    topic: z.string().min(3).max(400).optional(),
    templateId: z.string().uuid().optional(),
  })
  .strict();
export type CreateArticleArgs = z.infer<typeof createArticleArgs>;

/**
 * One id per invocation.
 *
 * The brief asks for `ids[]`, and the shape is kept so a batch can land later,
 * but it is capped at 1 today because a `CommitAuthorization` is ONE-USE and
 * `publishArticle` is a per-article seam: a two-id publish would mint one
 * authorization, spend it on the first article, and be refused on the second
 * with the first already live on the client site. Refusing up front beats a
 * half-applied batch nobody can undo.
 */
export const publishArticlesArgs = z
  .object({ ids: z.array(z.string().uuid()).min(1).max(1) })
  .strict();
export type PublishArticlesArgs = z.infer<typeof publishArticlesArgs>;

export const restartGenerationArgs = z
  .object({
    id: z.string().uuid(),
    /**
     * Named explicitly and never defaulted server-side. `blogCreationService`
     * .regenerate:664 omits it, so the backend falls back to the row's STORED
     * publish mode — which is how a restart can push a draft live. The tool
     * always sends a writer and always sends `wordpress_publish_mode: 'none'`.
     */
    modelOverride: z.string().min(2).max(80).default('qwen3.8-flash'),
  })
  .strict();
export type RestartGenerationArgs = z.infer<typeof restartGenerationArgs>;

/* -------------------------------------------------------------------------- */
/* planner_find_articles — the shared read                                    */
/* -------------------------------------------------------------------------- */

/** Re-exported so callers use ONE row shape; the contract owns the fields. */
export type ArticleRow = PlannerArticleSummary;

/** What `resolve` hands to `diff`/`run` for the read. */
export interface FoundArticles {
  readonly rows: readonly ArticleRow[];
  /** Total matches before the render cap, so the agent knows it is seeing a page. */
  readonly matched: number;
}

/**
 * A refusal that teaches. An empty result and a bad filter are different
 * outcomes and must not both render as "no articles": the first is a fact
 * about the tenant, the second is a fixable mistake.
 */
function noMatches(filterSummary: string): Refusal {
  return {
    kind: 'refused',
    because:
      `No articles matched ${filterSummary}. ` +
      `Valid status values are: ${ARTICLE_STATUSES.join(', ')}. ` +
      `Dates are YYYY-MM-DD, or use month as YYYY-MM.`,
    retryable: true,
  };
}

export function resolveFindArticles(
  args: FindArticlesArgs,
  rows: readonly ArticleRow[],
): FoundArticles | Refusal {
  if (rows.length === 0) return noMatches(describeFilters(args));
  return { rows: rows.slice(0, FIND_ROW_CAP), matched: rows.length };
}

export function describeFilters(a: FindArticlesArgs): string {
  const parts: string[] = [];
  if (a.ids?.length) parts.push(`${a.ids.length} id(s)`);
  if (a.status?.length) parts.push(`status in [${a.status.join(', ')}]`);
  if (a.title) parts.push(`title~"${a.title}"`);
  if (a.month) parts.push(`month ${a.month}`);
  if (a.from || a.to) parts.push(`${a.from ?? '…'}..${a.to ?? '…'}`);
  return parts.length ? parts.join(', ') : 'the current tenant';
}

/**
 * Receipt for the read. Ids are machine-readable in `facts.ids` because
 * `Receipt.facts` is primitive-only, so the write tools can consume them
 * without the agent re-parsing prose.
 */
export function formatFindReceipt(found: FoundArticles): Receipt {
  const lines = found.rows.map(
    (r) => `${r.id}  ${r.status}  ${truncate(r.title || '(untitled)', 60)}`,
  );
  const hidden = found.matched - found.rows.length;
  const summary =
    lines.join('\n') +
    (hidden > 0 ? `\n… ${hidden} more matched; narrow with status, title or month.` : '');
  return {
    summary,
    facts: {
      ids: found.rows.map((r) => r.id).join(','),
      count: found.rows.length,
      matched: found.matched,
    },
  };
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}

/* -------------------------------------------------------------------------- */
/* planner_publish_articles — the gate is pre-evaluated, never awaited        */
/* -------------------------------------------------------------------------- */

/** One article's pre-publish verdict, computed from `buildGateItem`. */
export interface GateVerdict {
  readonly id: string;
  readonly label: string;
  readonly violations: readonly string[];
}

export interface PublishTargets {
  readonly ids: readonly string[];
}

/**
 * `publish:549` and `batchPublish:706` AWAIT a human click on the app-root
 * gate provider. A tool that calls them with violations present does not fail,
 * it hangs until the 5s handshake timeout with the publish still pending. So
 * the gate is evaluated here, in `resolve`, and a violation is a Refusal.
 */
export function resolvePublishArticles(
  args: PublishArticlesArgs,
  verdicts: readonly GateVerdict[],
): PublishTargets | Refusal {
  const missing = args.ids.filter((id) => !verdicts.some((v) => v.id === id));
  if (missing.length > 0) {
    return {
      kind: 'refused',
      because: `Unknown article id(s): ${missing.join(', ')}.`,
      remedy: { command: 'planner_find_articles' as never, why: 'lists valid ids for this tenant' },
      candidates: verdicts.map((v) => ({ id: v.id, label: v.label })),
    };
  }
  const blocked = verdicts.filter((v) => args.ids.includes(v.id) && v.violations.length > 0);
  if (blocked.length > 0) {
    return {
      kind: 'refused',
      because:
        `Pre-publish gate blocks ${blocked.length} of ${args.ids.length}: ` +
        blocked
          .map((b) => `${truncate(b.label, 40)} (${b.violations.join('; ')})`)
          .join(' | ') +
        '. Fix the metadata, then publish. The gate is never auto-approved.',
      candidates: blocked.map((b) => ({ id: b.id, label: b.label })),
      retryable: true,
    };
  }
  return { ids: args.ids };
}

/* -------------------------------------------------------------------------- */
/* Diffs                                                                       */
/* -------------------------------------------------------------------------- */

/** A read changes nothing. The runtime still mints an authorization; harmless. */
export function emptyDiff(tenant: TenantId): Diff {
  return { table: 'blog_content', rowId: null, tenant, changes: [], reachesGeneratedContent: false };
}

export function publishDiff(target: PublishTargets, tenant: TenantId): Diff {
  return {
    table: 'blog_content',
    rowId: target.ids.length === 1 ? target.ids[0] : null,
    tenant,
    changes: target.ids.map((id) => ({
      field: `publish:${id}`,
      from: 'draft',
      to: 'live on the client site',
      authorability: 'exact' as const,
    })),
    reachesGeneratedContent: false,
  };
}

export function createDiff(args: CreateArticleArgs, tenant: TenantId): Diff {
  return {
    table: 'blog_content',
    rowId: null,
    tenant,
    changes: [
      { field: 'keyword', from: null, to: args.keyword, authorability: 'fact' as const },
      ...(args.title ? [{ field: 'title', from: null, to: args.title, authorability: 'prose' as const }] : []),
    ],
    reachesGeneratedContent: true,
  };
}

export function restartDiff(args: RestartGenerationArgs, tenant: TenantId): Diff {
  return {
    table: 'blog_content',
    rowId: args.id,
    tenant,
    changes: [
      { field: 'model_override', from: null, to: args.modelOverride, authorability: 'exact' as const },
      { field: 'wordpress_publish_mode', from: null, to: 'none', authorability: 'exact' as const },
    ],
    reachesGeneratedContent: true,
  };
}

/* -------------------------------------------------------------------------- */
/* Assembly                                                                    */
/*                                                                             */
/* `section: 'table'` is declared per the WebMCP lane's ruling. It is not yet  */
/* legal — `Command.section` is still `BbLetter` until Calendar's commit two   */
/* widens it to `BbLetter | PlannerSection`. That is the ONLY expected error   */
/* here, and it turns green on their second sha.                               */
/*                                                                             */
/* The dirty veto fails CLOSED on an unknown section, so until the table half  */
/* of ContentPlannerPage.tsx publishes a verdict for `table` through           */
/* useAgentFocus, every write below refuses rather than writing. That is the   */
/* safe direction, and it is why the emitter is a shipping requirement and not */
/* a nicety.                                                                   */
/* -------------------------------------------------------------------------- */

import { defineCommand, type Command, type Section } from '../contract';
import { resolveArticleWindow } from '../plannerDates';

/** Every planner tool lives in the table section for the dirty veto. */
const PLANNER_SECTION: Section = 'table';

export const plannerFindArticles: Command<FindArticlesArgs, FoundArticles> = defineCommand({
  name: 'planner_find_articles',
  section: PLANNER_SECTION,
  consequence: 'reads',
  // The one Table tool Emjae may call directly. It is the id source every
  // write depends on, and it changes nothing, so exposing it costs a refusal
  // at worst. The three writes leave this unset by design — the type forbids
  // `true` on them, and that is the point.
  exposeToEmjae: true,
  description:
    'List content-planner articles for the current workspace. Called with no arguments it lists ' +
    'the whole planner, newest first, with no date filter. Filter by ids, status, a title ' +
    'substring, or a date window (from/to as YYYY-MM-DD, or month as YYYY-MM); a date window ' +
    'matches the calendar placement date and lists only articles that have one. Returns ids in ' +
    'facts.ids for the write tools. Start here; every write needs ids from this tool.',
  args: findArticlesArgs,

  async resolve({ args, tenant, read, signal }) {
    if (!read.findArticles) {
      return { kind: 'refused', because: 'Article listing is unavailable on this page.' };
    }
    // `undefined` reaches the seam as "no window", which is what an argument
    // the caller never supplied has to mean. Passing a resolved range
    // unconditionally is how a bare call became "articles dated today".
    const dateWindow = resolveArticleWindow({ from: args.from, to: args.to, month: args.month });
    if ('kind' in dateWindow) return dateWindow;
    signal.throwIfAborted();
    const rows = await read.findArticles(tenant, {
      from: dateWindow.from ?? undefined,
      to: dateWindow.to ?? undefined,
      status: args.status,
      titleContains: args.title,
      limit: FIND_ROW_CAP + 1,
    });
    const byId = args.ids ? rows.filter((r) => args.ids!.includes(r.id)) : rows;
    return resolveFindArticles(args, byId);
  },

  diff(_target, tenant) {
    return emptyDiff(tenant);
  },

  async run(target) {
    return formatFindReceipt(target);
  },
});

export const plannerCreateArticle: Command<CreateArticleArgs, CreateArticleArgs> = defineCommand({
  name: 'planner_create_article',
  section: PLANNER_SECTION,
  consequence: 'spends-budget',
  description:
    'Create a new planner article from a keyword and start its generation. Replaces the ten-step ' +
    'creation wizard. Always creates with destination "none"; publishing is a separate, explicit tool.',
  args: createArticleArgs,

  async resolve({ args }) {
    return args;
  },

  diff(target, tenant) {
    return createDiff(target, tenant);
  },

  async run(target, auth, write) {
    if (!write.createArticle) {
      return { kind: 'refused', because: 'Article creation is unavailable on this page.' };
    }
    return write.createArticle(auth, {
      title: target.title ?? target.keyword,
      keyword: target.keyword,
      wordpressPublishMode: 'none',
    });
  },
});

export const plannerPublishArticles: Command<PublishArticlesArgs, PublishTargets> = defineCommand({
  name: 'planner_publish_articles',
  section: PLANNER_SECTION,
  consequence: 'writes-live-site',
  description:
    'Publish one finished article to the live client WordPress site. Refuses, with the exact ' +
    'violations, when the pre-publish metadata gate would block it. Get ids from planner_find_articles.',
  args: publishArticlesArgs,

  async resolve({ args, tenant, read, signal }) {
    if (!read.findArticles) {
      return { kind: 'refused', because: 'Article lookup is unavailable on this page.' };
    }
    signal.throwIfAborted();
    const rows = await read.findArticles(tenant, { limit: 200 });
    const verdicts: GateVerdict[] = rows.map((r) => ({
      id: r.id,
      label: r.title,
      // The gate is evaluated by the seam layer against ContentDetail; a row
      // that already has a WordPress post is the one case visible from the
      // summary, and republishing it is not what this tool is for.
      violations: r.hasWordPressPost ? ['already has a WordPress post'] : [],
    }));
    return resolvePublishArticles(args, verdicts);
  },

  diff(target, tenant) {
    return publishDiff(target, tenant);
  },

  async run(target, auth, write) {
    if (!write.publishArticle) {
      return { kind: 'refused', because: 'Publishing is unavailable on this page.' };
    }
    return write.publishArticle(auth, target.ids[0]);
  },
});

export const plannerRestartGeneration: Command<RestartGenerationArgs, RestartGenerationArgs> =
  defineCommand({
    name: 'planner_restart_generation',
    section: PLANNER_SECTION,
    consequence: 'spends-budget',
    description:
      'Re-run generation for one article with an explicit writer, never inheriting the writer or ' +
      'publish destination the previous run happened to use. Destination is always "none".',
    args: restartGenerationArgs,

    async resolve({ args }) {
      return args;
    },

    diff(target, tenant) {
      return restartDiff(target, tenant);
    },

    async run(target, auth, write) {
      if (!write.regenerate) {
        return { kind: 'refused', because: 'Restarting generation is unavailable on this page.' };
      }
      return write.regenerate(auth, target.id, {
        modelOverride: target.modelOverride,
        wordpressPublishMode: 'none',
      });
    },
  });
